/**
 * Enhanced Data Validation System for CondoShield CRM
 * JSON Schema validation + business rules + cross-field validation
 */

import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { getAssociationProperty } from './dataModelHelpers.js';

// Initialize AJV with formats
const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

// JSON Schema for association data validation
export const associationSchema = {
  type: 'object',
  required: ['name', 'status'],
  properties: {
    id: { type: 'string' },
    name: {
      type: 'string',
      minLength: 1,
      maxLength: 200,
      description: 'Association name is required',
    },
    address: {
      type: 'string',
      minLength: 5,
      maxLength: 500,
      description: 'Valid address required',
    },
    doors: {
      anyOf: [
        { type: 'string', pattern: '^\\d+$' },
        { type: 'number', minimum: 1, maximum: 10000 },
      ],
      description: 'Unit count must be between 1-10000',
    },
    monthly_revenue: {
      type: 'string',
      pattern: '^\\d+(\\.\\d+)?$',
      description: 'Monthly revenue must be a valid number string',
    },
    status: {
      type: 'string',
      enum: [
        'New',
        'Attempt to Contact',
        'Engaged',
        'Discovery',
        'Proposal',
        'Refinement',
        'Contract Sent',
        'Closed Lost',
        'Closed Won',
        'Closed Dead',
      ],
      description: 'Valid status required',
    },
    contactName: {
      type: 'string',
      minLength: 1,
      maxLength: 100,
      description: 'Contact name required',
    },
    contactEmail: {
      type: 'string',
      format: 'email',
      description: 'Valid email format required',
    },
    contactPhone: {
      type: 'string',
      pattern: '^\\+?[1-9]\\d{1,14}$',
      description: 'Valid phone number format required',
    },
    type: {
      type: 'string',
      enum: ['COA', 'HOA', 'Cooperative', 'Master-Planned', 'Unknown', 'New Build'],
      description: 'Association type must be a valid type',
    },
    lastActivity: {
      type: 'string',
      format: 'date-time',
      description: 'Valid ISO date-time format required',
    },
  },
  additionalProperties: true,
};

// Business rules validation
export const businessRules = {
  /**
   * Validate revenue-to-unit ratio (business logic)
   */
  validateRevenueUnitRatio: association => {
    const unitCount = getAssociationProperty.unitCount(association);
    const revenue = getAssociationProperty.monthlyRevenue(association);

    if (!unitCount || !revenue) return { valid: true }; // Skip if missing data

    const units = parseInt(unitCount) || 0;
    const monthlyRev = parseFloat(revenue) || 0;

    if (units === 0) return { valid: true }; // Skip if no units

    const revenuePerUnit = monthlyRev / units;

    // Business rules: reasonable revenue per unit ranges
    if (revenuePerUnit < 10) {
      return {
        valid: false,
        message: `Revenue per unit ($${revenuePerUnit.toFixed(2)}) seems unusually low`,
        severity: 'warning',
      };
    }

    if (revenuePerUnit > 10000) {
      return {
        valid: false,
        message: `Revenue per unit ($${revenuePerUnit.toFixed(2)}) seems unusually high`,
        severity: 'warning',
      };
    }

    return { valid: true };
  },

  /**
   * Validate contact information completeness
   */
  validateContactCompleteness: association => {
    const name = getAssociationProperty.contactName(association);
    const email = getAssociationProperty.contactEmail(association);
    const phone = getAssociationProperty.contactPhone(association);

    const hasName = name && name.trim().length > 0;
    const hasEmail = email && email.trim().length > 0;
    const hasPhone = phone && phone.trim().length > 0;

    if (!hasName && !hasEmail && !hasPhone) {
      return {
        valid: false,
        message: 'At least one contact method (name, email, or phone) is required',
        severity: 'error',
      };
    }

    if (hasEmail && !isValidEmail(email)) {
      return {
        valid: false,
        message: 'Contact email format is invalid',
        severity: 'error',
      };
    }

    if (hasPhone && !isValidPhone(phone)) {
      return {
        valid: false,
        message: 'Contact phone format is invalid',
        severity: 'error',
      };
    }

    return { valid: true };
  },

  /**
   * Validate status transition logic
   */
  validateStatusTransition: (currentStatus, newStatus) => {
    const validStatuses = [
      'New',
      'Attempt to Contact',
      'Engaged',
      'Discovery',
      'Proposal',
      'Refinement',
      'Contract Sent',
      'Closed Lost',
      'Closed Won',
      'Closed Dead',
    ];

    if (!validStatuses.includes(newStatus)) {
      return {
        valid: false,
        message: `Invalid status: ${newStatus}`,
        severity: 'error',
      };
    }

    // Business logic: prevent invalid transitions
    const closedStatuses = ['Closed Lost', 'Closed Won', 'Closed Dead'];

    if (closedStatuses.includes(currentStatus) && !closedStatuses.includes(newStatus)) {
      return {
        valid: false,
        message: 'Cannot reopen a closed association',
        severity: 'warning',
      };
    }

    return { valid: true };
  },

  /**
   * Validate data completeness for critical fields
   */
  validateDataCompleteness: association => {
    const issues = [];

    // Critical fields that should be present
    const criticalFields = [
      { key: 'name', display: 'Association Name' },
      { key: 'address', display: 'Address' },
      { key: 'doors', display: 'Unit Count' },
      { key: 'monthly_revenue', display: 'Monthly Revenue' },
      { key: 'type', display: 'Association Type' },
    ];

    criticalFields.forEach(field => {
      const value = association[field.key];
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        issues.push({
          field: field.key,
          message: `${field.display} is missing`,
          severity: 'warning',
        });
      }
    });

    return {
      valid: issues.length === 0,
      issues,
    };
  },

  /**
   * Validate if an association looks like a "stub" (erroneous entry)
   * A stub is defined as having a person's name as the association name
   * AND having minimal business data (<= 2 important fields).
   */
  validateStub: association => {
    const name = (association.name || association.associationName || '').toString().trim();
    if (!name) return { valid: true, isStub: false }; // Empty name handled by schema

    const nameLower = name.toLowerCase();
    const problematicNames = ['mario', 'jim', 'sarah', 'dave', 'nicole', 'alexis', 'elizabeth'];

    // Check if it contains any of the problematic names
    const containsProblematicName = problematicNames.some(problemName =>
      nameLower.includes(problemName)
    );

    if (!containsProblematicName) return { valid: true, isStub: false };

    // Patterns for these specific names
    const personNamePatterns = [
      /^(mario|jim|sarah|dave|nicole|alexis|elizabeth)\s+\w+$/i,
      /^(mr\.?|mrs\.?|ms\.?|dr\.?)\s+(mario|jim|sarah|dave|nicole|alexis|elizabeth)\s+\w+$/i,
      /^(mario|jim|sarah|dave|nicole|alexis|elizabeth)$/i,
      /^\w+\s+(mario|jim|sarah|dave|nicole|alexis|elizabeth)$/i,
      /^(mario|jim|sarah|dave|nicole|alexis|elizabeth)\s+\w+\s+\w+$/i,
    ];

    const looksLikePerson = personNamePatterns.some(pattern => pattern.test(name));

    if (!looksLikePerson) return { valid: true, isStub: false };

    // Check for minimal data
    const importantFields = [
      association.address,
      association.contact,
      association.contactName,
      association.email,
      association.contactEmail,
      association.phone,
      association.contactPhone,
      association.numberOfUnits,
      association.unitCount,
      association.managementCompany,
      association.notes,
      association.leadSource,
      association.engagementStrategy,
    ];

    const nonEmptyFields = importantFields.filter(
      field => field && field.toString().trim().length > 0
    ).length;

    // If it looks like a person name AND has 2 or fewer fields, it's a stub
    if (nonEmptyFields <= 2) {
      return {
        valid: false,
        isStub: true,
        message: `Blocked: "${name}" looks like a person's name rather than an association name, and the record has minimal data. Please provide a proper association name or more business details.`,
        severity: 'error',
      };
    }

    return { valid: true, isStub: false };
  },
};

// Utility functions
export const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = phone => {
  // Remove all non-digit characters except +
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  // Basic phone validation: 10-15 digits, may start with +
  const phoneRegex = /^\+?[1-9]\d{9,14}$/;
  return phoneRegex.test(cleanPhone);
};

// Main validation function
export const validateAssociation = (association, options = {}) => {
  const { skipBusinessRules = false, skipCompleteness = false, strict = false } = options;

  const results = {
    valid: true,
    errors: [],
    warnings: [],
    schemaErrors: [],
    businessRuleErrors: [],
    completenessIssues: [],
  };

  // JSON Schema validation
  try {
    const validate = ajv.compile(associationSchema);
    const schemaValid = validate(association);

    if (!schemaValid) {
      results.schemaErrors = validate.errors.map(err => ({
        field: err.instancePath.substring(1) || 'unknown',
        message: err.message,
        severity: 'error',
      }));
      results.valid = false;
    }
  } catch (error) {
    console.warn('JSON Schema validation error:', error.message);
  }

  // Business rules validation
  if (!skipBusinessRules) {
    const revenueRatio = businessRules.validateRevenueUnitRatio(association);
    if (!revenueRatio.valid) {
      results.businessRuleErrors.push(revenueRatio);
      if (revenueRatio.severity === 'error' || strict) {
        results.valid = false;
      }
    }

    const contactValidation = businessRules.validateContactCompleteness(association);
    if (!contactValidation.valid) {
      results.businessRuleErrors.push(contactValidation);
      if (contactValidation.severity === 'error' || strict) {
        results.valid = false;
      }
    }
  }

  // Data completeness validation
  if (!skipCompleteness) {
    const completeness = businessRules.validateDataCompleteness(association);
    if (!completeness.valid) {
      results.completenessIssues = completeness.issues;
      if (strict) {
        results.valid = false;
      }
    }
  }

  // Stub detection (always run unless explicitly skipped)
  const stubCheck = businessRules.validateStub(association);
  if (!stubCheck.valid) {
    results.businessRuleErrors.push(stubCheck);
    results.valid = false;
  }

  // Categorize all issues
  results.errors = [
    ...results.schemaErrors.filter(e => e.severity === 'error'),
    ...results.businessRuleErrors.filter(e => e.severity === 'error'),
  ];

  results.warnings = [
    ...results.schemaErrors.filter(e => e.severity === 'warning'),
    ...results.businessRuleErrors.filter(e => e.severity === 'warning'),
    ...results.completenessIssues,
  ];

  return results;
};

// Validation for bulk operations
export const validateAssociationBulk = (associations, options = {}) => {
  const results = {
    total: associations.length,
    valid: 0,
    invalid: 0,
    errors: [],
    warnings: [],
    details: [],
  };

  associations.forEach((association, index) => {
    const validation = validateAssociation(association, options);
    results.details.push({
      index,
      id: association.id,
      name: association.name,
      ...validation,
    });

    if (validation.valid) {
      results.valid++;
    } else {
      results.invalid++;
      results.errors.push(...validation.errors);
      results.warnings.push(...validation.warnings);
    }
  });

  return results;
};

// Export for use in components
export default {
  validateAssociation,
  validateAssociationBulk,
  businessRules,
  associationSchema,
  isValidEmail,
  isValidPhone,
};
